import React, { useContext } from "react"
import cn from "classnames"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileAlt } from "@fortawesome/pro-light-svg-icons/faFileAlt"
import TooltipTrigger from "../TooltipTrigger/TooltipTrigger"
import { TreeItem } from "@atlaskit/tree"
import { ItemId } from "@atlaskit/tree/types"
import "./Category.scss"
import { KnowledgeBasePermittedAction } from "../../models/knowledgeBase/permission"
import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { useAppSelector } from "../../store/hooks"
import { selectCurrentArticle } from "../../store/knowledgeBase/selectors"
import { selectFastRepliesCatalogHotKeys, selectFastRepliesOpenedElement } from "../../store/dialogs/selectors"
import { testId } from "../../utility/tests/testId/knowledgeBase"
import truncate from "lodash/truncate"
import { Link } from "react-router-dom"
import { buildArticleActionUrl } from "../../utility/knowledgeBase/articleUrlHelper"
import { ArticleAction } from "../../models/article"
import KnowledgeBaseContext from "../KnowledgeBaseProvider/KnowledgeBaseContext"
import { ROOT_CATEGORY_CODE } from "../../utility/knowledgeBase/knowledgeBase"
import HotKeysExtension from "../HotKeysExtension/HotKeysExtension"
import CategoryIcon from "./components/CategoryIcon/CategoryIcon"
import ToggleIcon from "../ToggleIcon/ToggleIcon"

const tNamespace = "knowledgeBase:"
const tooltipDelay = { show: 450, hide: 0 }
const categoryTitleMaxLength = 40

export type OnTitleClick = (categoryItem: TreeItem, catalogCode?: string, categoryCode?: string) => void

export interface CategoryProps {
    item: TreeItem
    onExpand: (itemId: ItemId) => void
    onCollapse: (itemId: ItemId) => void
    onTitleClick?: OnTitleClick
    onCanEdit: (permittedAction?: KnowledgeBasePermittedAction) => boolean
    isDragging?: boolean
    titleClassName?: string
    selected?: boolean
    showExtensions?: boolean
    extensions?: JSX.Element[]
    isRoot?: boolean
    icon?: IconProp | ((item: TreeItem) => IconProp)
    showContentOnHover?: boolean
    isHide?: boolean
    noChildren?: boolean
    asLink?: boolean
    isHotKeysEnabled?: boolean
    isCheckbox?: boolean
}

export const Category: React.FC<CategoryProps> = props => {
    const {
        item,
        onExpand,
        onCollapse,
        isDragging,
        titleClassName,
        onTitleClick,
        onCanEdit,
        selected,
        showExtensions,
        extensions,
        icon,
        isRoot = false,
        showContentOnHover,
        isHide = false,
        noChildren = false,
        asLink = false,
        isHotKeysEnabled = false,
        isCheckbox = false
    } = props

    const { t } = useTranslation()
    const {
        id,
        data: { title = "", catalogCode, symbolCode, permittedAction }
    } = item

    const canEdit = onCanEdit(permittedAction) || item.data.isQueue

    const getIcon = (item: TreeItem) => {
        if (noChildren) {
            return null
        }
        if (item.hasChildren) {
            const toggleCallback = () => (item.isExpanded ? onCollapse(item.id) : onExpand(item.id))
            return <CategoryIcon icon={<ToggleIcon isExpanded={item.isExpanded} />} onClick={toggleCallback} />
        }

        const getIconContainer = (icon: IconProp) => (
            <CategoryIcon
                icon={
                    <FontAwesomeIcon
                        icon={icon}
                        size="lg"
                        className={cn("category__article", isHide ?? "category__article_hide")}
                    />
                }
            />
        )

        if (!icon) {
            return getIconContainer(faFileAlt)
        } else if (typeof icon === "function") {
            return getIconContainer(icon(item))
        } else {
            return getIconContainer(icon)
        }
    }

    const { url } = useContext(KnowledgeBaseContext)

    const handleTitleClick = (event: React.MouseEvent<HTMLElement>) => {
        if ((event?.ctrlKey || event?.metaKey || event?.shiftKey) && item.data?.symbolCode !== ROOT_CATEGORY_CODE) {
            return
        }
        event?.preventDefault()
        if (onTitleClick) {
            onTitleClick(item, catalogCode, symbolCode)
        }
    }

    const currentArticle = useAppSelector(selectCurrentArticle)
    const isOpenedArticle: boolean = `${symbolCode}` === currentArticle?.Article?.SymbolCode

    const hotKeys = useAppSelector(selectFastRepliesCatalogHotKeys)
    const openedElement = useAppSelector(selectFastRepliesOpenedElement)
    const showHotKeyExtension = openedElement?.articleId === symbolCode

    const articleContent = item.data.answer
    const tooltipCondition = (showContentOnHover && Boolean(articleContent)) || title.length > categoryTitleMaxLength
    const tooltipContent = showContentOnHover && Boolean(articleContent) ? articleContent : title
    const tooltipId = typeof id === "string" ? id : String(id)

    const defaultProps = {
        "data-testid": testId.categoryTitleLink,
        className: cn("categoryLink", isCheckbox && "categoryLink_checkbox")
    }

    const Category = (
        <div
            className={cn(
                "category",
                selected || isOpenedArticle ? "category_selected" : undefined,
                showExtensions || (isHotKeysEnabled && showHotKeyExtension) ? "category_extensions-shown" : undefined,
                isDragging ? "category__drag" : undefined,
                isRoot ? "category__root" : undefined,
                isHide && "category_hide",
                noChildren && "category_no-icon-padding"
            )}
            data-testid={testId.category}
        >
            {getIcon(item)}

            <TooltipTrigger
                id={tooltipId}
                placement={showContentOnHover ? "auto" : "top"}
                content={truncate(tooltipContent, { length: 1000, omission: "..." })}
                condition={tooltipCondition}
                delay={tooltipDelay}
            >
                <div
                    className={cn(
                        isOpenedArticle ? "category__title_isOpenedArticle" : "category__title",
                        titleClassName
                    )}
                    onClick={handleTitleClick}
                    data-testid={testId.categoryTitle}
                >
                    {title ? title : t(`${tNamespace}untitled`)}
                </div>
            </TooltipTrigger>
            {canEdit && extensions}
            {isHotKeysEnabled && (
                <HotKeysExtension
                    currentArticleId={symbolCode}
                    categoryId={item.data.catalogCode}
                    hotKey={hotKeys?.find(hotKey => hotKey.ArticleSymbol === symbolCode)}
                />
            )}
        </div>
    )

    if (asLink) {
        return (
            <Link
                {...defaultProps}
                to={buildArticleActionUrl(url, ArticleAction.View, symbolCode)}
                rel={"noopener noreferrer"}
                onClick={e => e.preventDefault()}
            >
                {Category}
            </Link>
        )
    } else {
        return <div {...defaultProps}>{Category}</div>
    }
}
